import Mixpanel from 'mixpanel-browser';
import { MIXPANEL_TOKENS, SESSION_PROPERTIES, ANALYTICS_TO_TRACK_VIA_API } from 'shared/constants';
import { currentEnvironment, isLocalRuntime } from 'shared/services/Environment';
import { datadogRum } from '@datadog/browser-rum';
import { Executer } from 'shared/utils/Executer';
import AnalyticsAPI from 'shared/api/analytics.service.api';
import userService from 'shared/services/user.service';
import analyticsSession from './analytics-session';
import { getDeviceId } from './device-fingerprint';
import navigationContextManager from 'shared/helpers/src/navigation-context-manager';

class AnalyticsService {
  constructor() {
    this.sessionProperties = {
      [SESSION_PROPERTIES.CLIENT_APP]: 'web',
    };
    this._consentGranted = false;
    this.userPropertyKeys = [];
  }

  _isTrackingEnabled() {
    if (isLocalRuntime()) return process.env.REACT_APP_ANALYTICS_ENABLED === 'true';

    return true;
  }

  async init() {
    if (isLocalRuntime() && this._isTrackingEnabled()) {
      console.warn(
        '[Analytics] Tracking is ENABLED on a local machine — unset REACT_APP_ANALYTICS_ENABLED to stop sending usage data',
      );
    }

    Mixpanel.init(MIXPANEL_TOKENS[currentEnvironment()], {
      opt_out_tracking_by_default: true,
      opt_out_persistence_by_default: true,
    });

    if (this._isTrackingEnabled()) {
      datadogRum.init({
        applicationId: 'f7b46c0a-9450-4b2d-b088-69fd1da47963',
        clientToken: 'pub67d15f7b9cecd75ef58d72757e638ce8',
        site: 'datadoghq.eu',
        service: 'platform',
        env: currentEnvironment(),
        version: process.env.REACT_APP_RELEASE_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        trackingConsent: 'not-granted',
        compressIntakeRequests: true,
        excludedActivityUrls: [/browser-intake-datadoghq/],
      });
    }

    analyticsSession.init();
  }

  async grantConsent() {
    if (this._consentGranted) return;

    // Killer switch: local runs (incl. e2e) skip consent. track()/etc. already
    // no-op locally, so the opt-in is moot — and this avoids the CPU-heavy
    // FingerprintJS getDeviceId() below running on every fresh browser context,
    // a cold-boot cost that compounds under parallel e2e load.
    if (!this._isTrackingEnabled()) return;

    this._consentGranted = true;

    Mixpanel.opt_in_tracking();

    datadogRum.setTrackingConsent('granted');

    const deviceId = await getDeviceId();
    if (deviceId) {
      this.sessionProperties[SESSION_PROPERTIES.DEVICE_ID] = deviceId;
      Mixpanel.register({ [SESSION_PROPERTIES.DEVICE_ID]: deviceId });
      datadogRum.setGlobalContextProperty(SESSION_PROPERTIES.DEVICE_ID, deviceId);
      console.debug('[Analytics] Device ID registered:', deviceId);
    }
  }

  track(eventName, properties = {}, mixpanelOptions = {}, callback) {
    // Update activity on every track call
    analyticsSession.updateActivity();

    const eventData = {
      ...properties,
      ...this.sessionProperties,
      ...navigationContextManager.getContext(),
      [SESSION_PROPERTIES.SESSION_ID]: analyticsSession.getSessionId(),
    };

    console.debug(`[Analytics] Track event - ${eventName}`, eventData);

    // Killer switch — still fire the callback so callers that sequence work
    // after send (e.g. logout's reset) aren't stranded locally.
    if (!this._isTrackingEnabled()) {
      callback?.();
      return false;
    }

    // datadog/API tracking read the already-snapshotted eventData, so fire them
    // before handing Mixpanel the callback — that way any identity-mutating work
    // sequenced in the callback (e.g. logout's reset) runs only after all three
    // sinks have captured this event. Mixpanel invokes the callback on the
    // request's completion, which is already after these synchronous calls.
    datadogRum.addAction(eventName, eventData);

    if (ANALYTICS_TO_TRACK_VIA_API.includes(eventName) && userService.isAuthenticated) {
      AnalyticsAPI.trackEvent({ event: `${eventName} {proxy}`, properties: eventData });
    }

    Mixpanel.track(eventName, eventData, mixpanelOptions, callback);
  }

  reset() {
    console.debug('[Analytics] Reset analytics service');

    // Clear stored session data
    analyticsSession.destroy();
    this.userPropertyKeys = [];

    // Killer switch
    if (!this._isTrackingEnabled()) return false;

    // Reset mixpanel identity
    Mixpanel.reset();
  }

  setUserId(id) {
    console.debug(`[Analytics] Set user id - ${id}`);

    // Killer switch
    if (!this._isTrackingEnabled()) return false;

    Mixpanel.identify(id);
    datadogRum.setUser({ id });
  }

  registerUserData(data) {
    console.debug(`[Analytics] Set user data`, data);

    // Killer switch
    if (!this._isTrackingEnabled()) return false;

    const nextKeys = Object.keys(data);
    this.userPropertyKeys
      .filter(key => !nextKeys.includes(key))
      .forEach(key => Mixpanel.unregister(key));
    this.userPropertyKeys = nextKeys;

    Mixpanel.register(data);
  }

  setGroup({ key, value }) {
    console.debug(`[Analytics] Set group ${key} - ${value}`);

    // Killer switch
    if (!this._isTrackingEnabled()) return false;

    Mixpanel.set_group(key, value);
    datadogRum.setUserProperty(key, value);
    this.sessionProperties[key] = value;
    Mixpanel.register(this.sessionProperties);
  }

  userReady() {
    // Skip DataDog RUM on local machines
    if (!this._isTrackingEnabled()) return;

    datadogRum.startSessionReplayRecording();

    Executer.logError = (message, data) => {
      setTimeout(() => {
        const sessionReplayLink = datadogRum.getSessionReplayLink();

        datadogRum.addError(new Error('401'), {
          ...data,
          message,
          sessionReplayLink,
        });
      }, 0);
    };
  }

  getSessionReplayLink() {
    return datadogRum.getSessionReplayLink();
  }

  setSessionProperty(key, value) {
    this.sessionProperties[key] = value;
  }

  // Cleanup method for when the service is destroyed
  destroy() {
    // Killer switch
    if (!this._isTrackingEnabled()) return false;

    analyticsSession.destroy();
  }
}

export default new AnalyticsService();
